import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Clock, Plus, Minus, TrendingUp, Moon, Briefcase, Timer, Calendar, ArrowLeftRight, Calculator, BookOpen, HelpCircle, Info, Shield, Mail, ChevronDown, ChevronUp } from 'lucide-react';

const sidebarGroups = [
  {
    label: 'Calculadoras',
    items: [
      { path: '/', label: 'Somar horas', icon: Plus },
      { path: '/calculadora-de-horas-trabalhadas', label: 'Horas trabalhadas', icon: Clock },
      { path: '/calculadora-de-horas-extras', label: 'Horas extras', icon: TrendingUp },
      { path: '/calculadora-de-subtracao-de-horas', label: 'Subtração', icon: Minus },
      { path: '/calculadora-de-tempo', label: 'Calculadora de tempo', icon: Timer },
      { path: '/calculadora-de-minutos', label: 'Minutos', icon: Timer },
      { path: '/contador-de-horas', label: 'Contador de horas', icon: Clock },
    ]
  },
  {
    label: 'CLT & Noturno',
    items: [
      { path: '/calculadora-de-adicional-noturno', label: 'Adicional noturno', icon: Moon },
      { path: '/adicional-noturno-valor', label: 'Valor noturno', icon: Moon },
      { path: '/adicional-noturno-horario', label: 'Horário noturno', icon: Moon },
      { path: '/calculadora-de-banco-de-horas', label: 'Banco de horas', icon: Briefcase },
      { path: '/banco-de-horas-como-funciona', label: 'Como funciona banco', icon: BookOpen },
    ]
  },
  {
    label: 'Guias & Conversores',
    items: [
      { path: '/converter-horas-em-dias', label: 'Horas → dias', icon: ArrowLeftRight },
      { path: '/jornada-de-trabalho', label: 'Jornada de trabalho', icon: Calendar },
      { path: '/calculo-de-hora-trabalhada', label: 'Valor da hora', icon: Calculator },
      { path: '/calculo-de-horas-extraordinarias', label: 'Horas extraordinárias', icon: BookOpen },
      { path: '/quantas-horas-extras-por-dia', label: 'Limite de extras', icon: HelpCircle },
    ]
  },
  {
    label: 'Institucional',
    items: [
      { path: '/sobre', label: 'Sobre', icon: Info },
      { path: '/politica-privacidade', label: 'Privacidade', icon: Shield },
      { path: '/contato', label: 'Contato', icon: Mail },
    ]
  }
];

const tabItems = [
  { path: '/', label: 'Somar', icon: Plus },
  { path: '/calculadora-de-horas-trabalhadas', label: 'Trabalhadas', icon: Clock },
  { path: '/calculadora-de-horas-extras', label: 'Extras', icon: TrendingUp },
  { path: '/calculadora-de-adicional-noturno', label: 'Noturno', icon: Moon },
  { path: '/calculadora-de-banco-de-horas', label: 'Banco', icon: Briefcase },
];

const Navigation = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({ 'Calculadoras': true, 'CLT & Noturno': true, 'Guias & Conversores': true, 'Institucional': true });
  const location = useLocation();
  const isActive = (path) => location.pathname === path;

  const toggleGroup = (label) => {
    setExpandedGroups(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const SidebarContent = ({ onLinkClick }) => (
    <div className="flex flex-col h-full overflow-y-auto">
      <Link to="/" onClick={onLinkClick} className="flex items-center px-5 py-4 border-b border-border-gray">
        <span className="text-lg font-heading font-bold text-blue-primary leading-tight">
          Calculadora<br />de Horas
        </span>
      </Link>
      <div className="flex-1 py-3">
        {sidebarGroups.map((group) => (
          <div key={group.label} className="mb-1">
            <button
              onClick={() => toggleGroup(group.label)}
              className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-body-text uppercase tracking-wider hover:text-blue-primary transition-colors"
            >
              {group.label}
              {expandedGroups[group.label]
                ? <ChevronUp size={12} />
                : <ChevronDown size={12} />}
            </button>
            {expandedGroups[group.label] && (
              <div className="mt-0.5">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={onLinkClick}
                      className={`flex items-center gap-2.5 px-4 py-2 text-sm transition-colors ${
                        isActive(item.path)
                          ? 'bg-blue-primary bg-opacity-10 text-blue-primary font-semibold border-r-2 border-blue-primary'
                          : 'text-body-text hover:bg-light-bg hover:text-blue-primary'
                      }`}
                    >
                      <Icon size={14} className="flex-shrink-0" />
                      <span className="leading-tight">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {/* ── DESKTOP: persistent left sidebar ── */}
      <aside className="hidden lg:flex flex-col fixed left-0 top-0 h-full w-56 bg-white border-r border-border-gray z-40">
        <SidebarContent onLinkClick={() => {}} />
      </aside>

      {/* ── MOBILE: top bar ── */}
      <nav className="lg:hidden sticky top-0 z-50 bg-white border-b border-border-gray">
        <div className="flex items-center justify-between h-14 px-4">
          <Link to="/" className="text-lg font-heading font-bold text-blue-primary">
            Calculadora de Horas
          </Link>
          <button
            onClick={() => setDrawerOpen(true)}
            className="p-2 rounded-md text-body-text hover:text-blue-primary hover:bg-light-bg"
            aria-label="Abrir menu"
            data-testid="mobile-menu-button"
          >
            <Menu size={22} />
          </button>
        </div>
      </nav>

      {/* ── MOBILE: full-screen drawer ── */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex justify-end">
          <div
            className="absolute inset-0 bg-black bg-opacity-40"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="relative w-72 max-w-full bg-white h-full overflow-hidden flex flex-col shadow-xl animate-slide-in-right" data-testid="mobile-menu">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border-gray">
              <span className="text-lg font-heading font-bold text-blue-primary">Todas as ferramentas</span>
              <button onClick={() => setDrawerOpen(false)} aria-label="Fechar menu" className="p-1 rounded text-body-text hover:text-blue-primary">
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <SidebarContent onLinkClick={() => setDrawerOpen(false)} />
            </div>
          </div>
        </div>
      )}

      {/* ── MOBILE: bottom tab bar ── */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-border-gray">
        <div className="flex">
          {tabItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex-1 flex flex-col items-center justify-center py-2 gap-0.5 transition-colors ${
                  active ? 'text-blue-primary' : 'text-body-text hover:text-blue-primary'
                }`}
              >
                <Icon size={20} strokeWidth={active ? 2.5 : 1.8} />
                <span className="text-xs font-medium leading-none">{item.label}</span>
                {active && <span className="absolute bottom-0 w-8 h-0.5 bg-blue-primary rounded-t" />}
              </Link>
            );
          })}
        </div>
      </nav>
    </>
  );
};

export default Navigation;
